import * as THREE from "three";
import type { FlowStep, SimulationSnapshot } from "../core/contracts";
import { PALETTE } from "./palette";
import type { CityWorld } from "./city";

interface Vehicle {
  mesh: THREE.Mesh;
  halo: THREE.Mesh;
}

const MAX_VEHICLES = 8;

export class FlowVisualizer {
  private readonly vehicles: Vehicle[] = [];
  private readonly shockwave: THREE.Mesh;
  private lastStepIndex = -1;
  private curve: THREE.CatmullRomCurve3 | null = null;
  private lastFlow = "";
  private readonly server = new THREE.Group();
  private route: THREE.Line | null = null;

  constructor(private readonly world: CityWorld) {
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(0.7, 0.4, 1.4),
      new THREE.MeshStandardMaterial({
        color: PALETTE.yellow,
        emissive: PALETTE.yellow,
        emissiveIntensity: 0.6,
      }),
    );
    const cabin = new THREE.Mesh(
      new THREE.BoxGeometry(0.55, 0.28, 0.5),
      new THREE.MeshStandardMaterial({
        color: 0x162d49,
        emissive: PALETTE.blue,
        emissiveIntensity: 0.4,
      }),
    );
    cabin.position.set(0, 0.3, 0.22);
    this.server.add(body, cabin);
    this.world.add(this.server);
    for (let index = 0; index < MAX_VEHICLES; index += 1) {
      const meshMaterial = new THREE.MeshStandardMaterial({
        color: PALETTE.white,
        emissive: PALETTE.blue,
        emissiveIntensity: 5,
        metalness: 0.15,
        roughness: 0.18,
      });
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(0.34, 0.34, 0.55),
        meshMaterial,
      );
      mesh.visible = false;
      this.world.add(mesh);

      const halo = new THREE.Mesh(
        new THREE.RingGeometry(0.34, 0.48, 24),
        new THREE.MeshBasicMaterial({
          color: PALETTE.blue,
          transparent: true,
          opacity: 0.58,
          side: THREE.DoubleSide,
        }),
      );
      halo.rotation.x = -Math.PI / 2;
      halo.visible = false;
      this.world.add(halo);
      this.vehicles.push({ mesh, halo });
    }

    this.shockwave = new THREE.Mesh(
      new THREE.RingGeometry(0.6, 0.72, 48),
      new THREE.MeshBasicMaterial({
        color: PALETTE.yellow,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
      }),
    );
    this.shockwave.rotation.x = -Math.PI / 2;
    this.world.add(this.shockwave);
  }

  update(snapshot: SimulationSnapshot): void {
    const step = snapshot.steps[snapshot.activeStepIndex];
    if (!step) {
      this.hide();
      return;
    }

    this.world.setMotionPaused(snapshot.paused || !snapshot.running);
    this.world.setActiveStep(step, snapshot.stepProgress);
    this.world.updateInterior(step, snapshot.stepProgress);
    if (
      snapshot.activeStepIndex !== this.lastStepIndex ||
      snapshot.flow !== this.lastFlow
    ) {
      this.createCurve(snapshot, step);
      this.lastStepIndex = snapshot.activeStepIndex;
      this.lastFlow = snapshot.flow;
    }

    const count = snapshot.flow === "ore" ? 0 : this.vehicleCount(step);
    const color = this.colorForStep(step);
    for (let index = 0; index < this.vehicles.length; index += 1) {
      const vehicle = this.vehicles[index]!;
      const visible =
        index < count && (snapshot.running || snapshot.stepProgress > 0);
      vehicle.mesh.visible = visible;
      vehicle.halo.visible = visible;
      if (!visible) {
        continue;
      }

      const stagger = Math.max(
        0,
        Math.min(1, snapshot.stepProgress * 1.35 - index * 0.075),
      );
      const point =
        this.curve?.getPoint(stagger) ??
        this.world.districtPosition(step.district);
      const laneOffset = (index - (count - 1) / 2) * 0.34;
      point.x += laneOffset;
      point.y += Math.sin(snapshot.stepProgress * Math.PI * 2 + index) * 0.08;
      vehicle.mesh.position.copy(point);
      vehicle.halo.position.copy(point).add(new THREE.Vector3(0, -0.25, 0));
      vehicle.mesh.rotation.set(0, 0, 0);
      vehicle.mesh.scale.set(
        step.action === "packet" ? 1.5 : 1,
        step.action === "packet" || step.action === "rows-return" ? 0.18 : 1,
        1,
      );

      const meshMaterial = vehicle.mesh.material as THREE.MeshStandardMaterial;
      meshMaterial.color.setHex(color);
      meshMaterial.emissive.setHex(color);
      const haloMaterial = vehicle.halo.material as THREE.MeshBasicMaterial;
      haloMaterial.color.setHex(color);
      const scale = 1 + Math.sin(performance.now() * 0.014 + index) * 0.15;
      vehicle.halo.scale.setScalar(scale);
    }

    const cameraProgress = Math.min(1, snapshot.stepProgress / 0.35);
    const cameraPoint =
      this.curve?.getPoint(cameraProgress) ??
      this.world.districtPosition(step.district);
    const cameraTangent =
      this.curve?.getTangent(cameraProgress) ?? new THREE.Vector3(0, 0, -1);
    this.server.visible = step.phase !== "io" && step.phase !== "buffer-cache";
    this.server.position.copy(cameraPoint).add(new THREE.Vector3(0, 0.6, 0));
    this.server.rotation.y = Math.atan2(cameraTangent.x, cameraTangent.z);
    this.world.followJourney(
      cameraPoint,
      cameraTangent,
      step,
      snapshot.stepProgress,
    );

    this.updateShockwave(snapshot, step, color);
  }

  private createCurve(snapshot: SimulationSnapshot, step: FlowStep): void {
    const previous =
      snapshot.steps[Math.max(0, snapshot.activeStepIndex - 1)] ?? step;
    const start = this.world.districtPosition(previous.district);
    const end = this.world.districtPosition(step.district);

    if (start.distanceTo(end) < 0.2) {
      start.add(new THREE.Vector3(-0.9, 0.2, 0.5));
      end.add(new THREE.Vector3(0.9, 0.4, -0.5));
    }

    const distance = start.distanceTo(end);
    const midpoint = start
      .clone()
      .lerp(end, 0.5)
      .add(new THREE.Vector3(0, Math.min(4.2, 1.1 + distance * 0.12), 0));
    this.curve = new THREE.CatmullRomCurve3([start, midpoint, end]);
    if (this.route) {
      this.route.removeFromParent();
      this.route.geometry.dispose();
      (this.route.material as THREE.Material).dispose();
    }
    this.route = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(this.curve.getPoints(50)),
      new THREE.LineBasicMaterial({
        color: PALETTE.yellow,
        transparent: true,
        opacity: 0.65,
      }),
    );
    this.world.add(this.route);
  }

  private updateShockwave(
    snapshot: SimulationSnapshot,
    step: FlowStep,
    color: number,
  ): void {
    const active = [
      "mutex-acquire",
      "child-reject",
      "child-create",
      "latch-acquire",
      "hash-chain",
    ].includes(step.action);
    const shockMaterial = this.shockwave.material as THREE.MeshBasicMaterial;
    if (!active) {
      shockMaterial.opacity = 0;
      return;
    }
    this.shockwave.position.copy(this.world.districtPosition(step.district));
    this.shockwave.position.y -= 1.7;
    shockMaterial.color.setHex(color);
    shockMaterial.opacity = 0.72 * (1 - snapshot.stepProgress);
    this.shockwave.scale.setScalar(0.8 + snapshot.stepProgress * 4.5);
  }

  private vehicleCount(step: FlowStep): number {
    if (step.action === "block-flight") {
      return step.id === "multi-block-flight" ? 4 : 1;
    }
    if (step.action === "rows-return") {
      return 6;
    }
    if (step.action === "io-queue" && step.event.includes("scattered")) {
      return 4;
    }
    if (step.action === "plan-node") {
      return 2;
    }
    return 1;
  }

  private colorForStep(step: FlowStep): number {
    if (step.action === "child-reject") {
      return PALETTE.red;
    }
    if (
      step.event.includes("mutex") ||
      step.event.includes("latch") ||
      step.event.includes("sequential") ||
      step.action === "child-create"
    ) {
      return PALETTE.yellow;
    }
    return PALETTE.blue;
  }

  private hide(): void {
    this.server.visible = false;
    this.lastStepIndex = -1;
    for (const vehicle of this.vehicles) {
      vehicle.mesh.visible = false;
      vehicle.halo.visible = false;
    }
    (this.shockwave.material as THREE.MeshBasicMaterial).opacity = 0;
    this.world.resetTourCamera();
  }
}
